import React, { useState, useEffect } from 'react';
import { TrendingUp, PlusCircle, Compass, CheckCircle, Sparkles, Lightbulb, Quote, PartyPopper, Heart, Search, Star, Target } from 'lucide-react';

interface Citation {
  id: string;
  date: string;
  snippet: string;
}

interface ActionCard {
  type: 'TRACK' | 'DECIDE' | 'VALUE' | 'GOAL';
  title: string;
  reason?: string;
}

interface ChatCardTranslations {
  support: string;
  celebration: string;
  exploration: string;
  reflection: string;
  coaching: string;
  insight?: string;
  basedOn: string;
  usingContext: string;
  rememberThis: string;
  saved: string;
  goDeeper: string;
  balanced: string;
  modeSupport: string;
  modeCelebration: string;
  modeExploration: string;
  lifeIntelligence: string;
  source?: string;
  sources?: string;
  memoryWhisper?: string;
  moments?: string;
  people?: string;
  places?: string;
  decisions?: string;
  usedMemories?: string;
  values?: string;
  goals?: string;
  patterns?: string;
  iSee?: string;
  iNotice?: string;
  microAction?: string;
  sintonia?: string;
  sintoniaHigh?: string;
  sintoniaMedium?: string;
  sintoniaLow?: string;
  basedOnYourHistory?: string;
  fromYourPatterns?: string;
  connectedTo?: string;
  saveValue?: string;
  saveGoal?: string;
}

interface ChatCardProps {
  content: string;
  timestamp: string;
  citedEntries?: Citation[];
  patternsUsed?: string[];
  valuesUsed?: string[];
  goalsUsed?: string[];
  peopleUsed?: string[];
  placesUsed?: string[];
  decisionsUsed?: string[];
  emotionalMode?: 'struggle' | 'celebration' | 'neutral' | 'curiosity';
  responseType?: 'support' | 'coaching' | 'exploration' | 'celebration' | 'reflection' | 'insight';
  actions?: ActionCard[];
  isExecuted?: (title: string) => boolean;
  onAction?: (type: string, title: string) => void;
  isGreeting?: boolean;
  enableTyping?: boolean;
  messageIndex?: number;
  translations?: ChatCardTranslations;
}

// Typing effect hook - word-by-word for natural Apple-like streaming
const useTypingEffect = (text: string, speed: number = 50, enabled: boolean = true) => {
  const [displayedText, setDisplayedText] = useState(enabled ? '' : text);
  const [isComplete, setIsComplete] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setDisplayedText(text);
      setIsComplete(true);
      return;
    }

    setDisplayedText('');
    setIsComplete(false);
    
    if (!text) return;

    // Split into words, keeping spaces/punctuation for natural reading
    const words = text.split(/(\s+)/);
    let wordIndex = 0;
    let accumulated = '';
    
    const timer = setInterval(() => {
      if (wordIndex < words.length) {
        // Add one word at a time with natural pacing
        accumulated += words[wordIndex];
        wordIndex++;
        setDisplayedText(accumulated);
      } else {
        setIsComplete(true);
        clearInterval(timer);
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed, enabled]);

  return { displayedText, isComplete };
};

const defaultTranslations: ChatCardTranslations = {
  support: 'Support',
  celebration: 'Celebration',
  exploration: 'Exploration',
  reflection: 'Reflection',
  coaching: 'Coaching',
  insight: 'Insight',
  basedOn: 'Based on',
  usingContext: 'Using',
  rememberThis: 'Remember this',
  saved: 'Saved',
  goDeeper: 'Go deeper',
  balanced: 'Balanced',
  modeSupport: 'Support',
  modeCelebration: 'Celebration',
  modeExploration: 'Exploration',
  lifeIntelligence: 'Life Intelligence',
  source: 'source',
  sources: 'sources',
  memoryWhisper: 'memories',
  moments: 'moments',
  people: 'people',
  places: 'places',
  decisions: 'decisions',
  usedMemories: 'memories used',
  values: 'values',
  goals: 'goals',
  patterns: 'patterns',
  iSee: 'I see that',
  iNotice: 'I notice that',
  microAction: 'Try this',
  sintonia: 'Alignment',
  sintoniaHigh: 'In harmony',
  sintoniaMedium: 'Needs tuning',
  sintoniaLow: 'Attention',
  basedOnYourHistory: 'From your history',
  fromYourPatterns: 'From your patterns',
  connectedTo: 'Connected to'
};

// Context Chip Component - Apple-quality inline indicator with full label visibility
const ContextChip: React.FC<{
  type: 'value' | 'goal' | 'pattern' | 'moment' | 'person' | 'place' | 'decision';
  label: string;
  typeLabel: string;
  isExpanded: boolean;
  onTap: () => void;
}> = ({ type, label, typeLabel, isExpanded, onTap }) => {
  const colors = {
    value: { dot: 'bg-emerald-500', bg: 'bg-emerald-50/80', text: 'text-emerald-700', border: 'border-emerald-200/60', shadow: 'shadow-emerald-100' },
    goal: { dot: 'bg-sky-500', bg: 'bg-sky-50/80', text: 'text-sky-700', border: 'border-sky-200/60', shadow: 'shadow-sky-100' },
    pattern: { dot: 'bg-amber-500', bg: 'bg-amber-50/80', text: 'text-amber-700', border: 'border-amber-200/60', shadow: 'shadow-amber-100' },
    moment: { dot: 'bg-violet-400', bg: 'bg-violet-50/80', text: 'text-violet-700', border: 'border-violet-200/60', shadow: 'shadow-violet-100' },
    person: { dot: 'bg-pink-500', bg: 'bg-pink-50/80', text: 'text-pink-700', border: 'border-pink-200/60', shadow: 'shadow-pink-100' },
    place: { dot: 'bg-teal-500', bg: 'bg-teal-50/80', text: 'text-teal-700', border: 'border-teal-200/60', shadow: 'shadow-teal-100' },
    decision: { dot: 'bg-orange-500', bg: 'bg-orange-50/80', text: 'text-orange-700', border: 'border-orange-200/60', shadow: 'shadow-orange-100' }
  };
  
  const c = colors[type];
  
  // Apple-style: always show as elegant pill with full label
  return (
    <button
      onClick={onTap}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${c.bg} ${c.border} border backdrop-blur-sm transition-all duration-200 hover:shadow-sm active:scale-[0.97] ${isExpanded ? `shadow-sm ${c.shadow}` : ''}`}
    >
      <span className={`w-2 h-2 rounded-full ${c.dot} shrink-0`} />
      <span className={`text-[10px] font-medium ${c.text} leading-tight`}>
        {label}
      </span>
    </button>
  );
};

// Mood Indicator Component - Compact colored dot with tooltip
const MoodIndicator: React.FC<{
  emotionalMode: 'struggle' | 'celebration' | 'curiosity' | 'neutral';
}> = ({ emotionalMode }) => {
  const moodConfig = {
    celebration: { color: 'bg-emerald-400', ring: 'ring-emerald-200', pulse: true },
    curiosity: { color: 'bg-amber-400', ring: 'ring-amber-200', pulse: false },
    struggle: { color: 'bg-blue-400', ring: 'ring-blue-200', pulse: false },
    neutral: { color: 'bg-gray-300', ring: 'ring-gray-100', pulse: false }
  };
  const config = moodConfig[emotionalMode];
  
  return (
    <div className={`w-2.5 h-2.5 rounded-full ${config.color} ring-2 ${config.ring} ${config.pulse ? 'animate-pulse' : ''}`} />
  );
};

// Insight Block Component - Visual highlight for AI observations about the user
const InsightBlock: React.FC<{
  content: string;
  type: 'observation' | 'pattern' | 'alignment' | 'action';
  translations: ChatCardTranslations;
  alignmentLevel?: 'high' | 'medium' | 'low';
}> = ({ content, type, translations, alignmentLevel }) => {
  const config = {
    observation: { 
      bg: 'bg-gradient-to-r from-violet-50 to-purple-50', 
      border: 'border-violet-200',
      icon: '👁️',
      label: translations.iSee || 'I see that',
      textColor: 'text-violet-800'
    },
    pattern: { 
      bg: 'bg-gradient-to-r from-amber-50 to-orange-50', 
      border: 'border-amber-200',
      icon: '🔄',
      label: translations.fromYourPatterns || 'From your patterns',
      textColor: 'text-amber-800'
    },
    alignment: { 
      bg: alignmentLevel === 'high' ? 'bg-gradient-to-r from-emerald-50 to-teal-50' :
          alignmentLevel === 'low' ? 'bg-gradient-to-r from-rose-50 to-red-50' :
          'bg-gradient-to-r from-amber-50 to-yellow-50', 
      border: alignmentLevel === 'high' ? 'border-emerald-200' :
              alignmentLevel === 'low' ? 'border-rose-200' :
              'border-amber-200',
      icon: alignmentLevel === 'high' ? '✅' : alignmentLevel === 'low' ? '⚠️' : '⚖️',
      label: alignmentLevel === 'high' ? (translations.sintoniaHigh || 'In harmony') :
             alignmentLevel === 'low' ? (translations.sintoniaLow || 'Attention') :
             (translations.sintoniaMedium || 'Needs tuning'),
      textColor: alignmentLevel === 'high' ? 'text-emerald-800' :
                 alignmentLevel === 'low' ? 'text-rose-800' :
                 'text-amber-800'
    },
    action: { 
      bg: 'bg-gradient-to-r from-sky-50 to-blue-50', 
      border: 'border-sky-200',
      icon: '✨',
      label: translations.microAction || 'Try this',
      textColor: 'text-sky-800'
    }
  };
  const c = config[type];
  
  return (
    <div className={`${c.bg} ${c.border} border rounded-2xl p-4 my-3 animate-in fade-in slide-in-from-left-2 duration-300`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-white/80 flex items-center justify-center shrink-0 shadow-sm">
          <span className="text-base">{c.icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[10px] font-bold uppercase tracking-wider text-black/40 block mb-1">{c.label}</span>
          <p className={`text-[14px] leading-relaxed ${c.textColor}`}>{content}</p>
        </div>
      </div>
    </div>
  );
};

const ChatCard: React.FC<ChatCardProps> = ({
  content,
  timestamp,
  citedEntries = [],
  patternsUsed = [],
  valuesUsed = [],
  goalsUsed = [],
  peopleUsed = [],
  placesUsed = [],
  decisionsUsed = [],
  emotionalMode = 'neutral',
  responseType = 'support',
  actions = [],
  isExecuted,
  onAction,
  isGreeting = false,
  enableTyping = false,
  messageIndex = 0,
  translations = defaultTranslations
}) => {
  const t = translations;
  const [expandedCitation, setExpandedCitation] = useState<string | null>(null);
  const [expandedChip, setExpandedChip] = useState<string | null>(null);
  
  // Check if any context sources are being used
  const hasContextUsed = valuesUsed.length > 0 || goalsUsed.length > 0 || patternsUsed.length > 0 || citedEntries.length > 0 || peopleUsed.length > 0 || placesUsed.length > 0 || decisionsUsed.length > 0;
  
  // Use typing effect for new messages
  const { displayedText, isComplete } = useTypingEffect(content, 12, enableTyping);

  const getModeLabel = () => {
    switch (emotionalMode) {
      case 'struggle': return { label: t.modeSupport, color: 'blue', icon: '💙' };
      case 'celebration': return { label: t.modeCelebration, color: 'green', icon: '🎉' };
      case 'curiosity': return { label: t.modeExploration, color: 'amber', icon: '🔍' };
      default: return { label: t.balanced, color: 'gray', icon: '⚖️' };
    }
  };

  const mode = getModeLabel();

  // Dynamic styling based on response type
  const getResponseTypeStyle = () => {
    switch (responseType) {
      case 'celebration':
        return { 
          border: 'border-emerald-200', 
          gradient: 'from-emerald-50 to-green-50', 
          accent: 'emerald',
          headerBg: 'bg-gradient-to-r from-emerald-100 to-green-100',
          icon: PartyPopper,
          iconColor: 'text-emerald-600',
          label: t.celebration,
          showHeader: true
        };
      case 'coaching':
        return { 
          border: 'border-orange-200', 
          gradient: 'from-orange-50 to-amber-50', 
          accent: 'orange',
          headerBg: 'bg-gradient-to-r from-orange-100 to-amber-100',
          icon: Lightbulb,
          iconColor: 'text-orange-600',
          label: t.coaching,
          showHeader: true
        };
      case 'exploration':
        return { 
          border: 'border-amber-200', 
          gradient: 'from-amber-50 to-yellow-50', 
          accent: 'amber',
          headerBg: 'bg-gradient-to-r from-amber-100 to-yellow-100',
          icon: Search,
          iconColor: 'text-amber-600',
          label: t.exploration,
          showHeader: true
        };
      case 'reflection':
        return { 
          border: 'border-slate-200', 
          gradient: 'from-slate-50 to-gray-50', 
          accent: 'slate',
          headerBg: 'bg-gradient-to-r from-slate-100 to-gray-100',
          icon: Quote,
          iconColor: 'text-slate-600',
          label: t.reflection,
          showHeader: true
        };
      case 'insight':
        return { 
          border: 'border-violet-200', 
          gradient: 'from-violet-50 to-purple-50', 
          accent: 'violet',
          headerBg: 'bg-gradient-to-r from-violet-100 to-purple-100',
          icon: Sparkles,
          iconColor: 'text-violet-600',
          label: t.insight || 'Insight',
          showHeader: true
        };
      default: // support
        return { 
          border: 'border-blue-200', 
          gradient: 'from-blue-50 to-indigo-50', 
          accent: 'blue',
          headerBg: 'bg-gradient-to-r from-blue-100 to-indigo-100',
          icon: Heart,
          iconColor: 'text-blue-600',
          label: t.support,
          showHeader: false
        };
    }
  };
  const typeStyle = getResponseTypeStyle();
  const TypeIcon = typeStyle.icon;

  const parseContentSections = (text: string) => {
    const lines = text.split('\n');
    type SectionType = 'text' | 'highlight' | 'list' | 'question' | 'quote' | 'insight-observation' | 'insight-pattern' | 'insight-alignment' | 'insight-alignment-high' | 'insight-alignment-low' | 'insight-action' | 'action-short' | 'action-medium' | 'action-long';
    const sections: Array<{ type: SectionType; content: string }> = [];
    let currentText = '';
    
    // Insight detection patterns with capture groups for stripping (multilingual)
    const observationPatterns = [
      /^(vedo che|noto che|ho notato che|mi accorgo che)\s*/i,
      /^(i see that|i notice that|i've noticed that|i observed that)\s*/i,
      /^(veo que|noto que|he notado que|observo que)\s*/i,
      /^(ich sehe dass|ich bemerke dass|mir ist aufgefallen dass)\s*/i,
      /^(je vois que|je remarque que|j'ai remarqué que)\s*/i,
      /^👁️\s*/
    ];
    const patternPatterns = [
      /^(questo pattern|il tuo pattern|dai tuoi pattern|questo schema)[:\s]*/i,
      /^(this pattern|your pattern|from your patterns|i've noticed a pattern)[:\s]*/i,
      /^(este patrón|tu patrón|de tus patrones)[:\s]*/i,
      /^(dieses muster|dein muster|aus deinen mustern)[:\s]*/i,
      /^(ce schéma|ton schéma|de tes schémas)[:\s]*/i,
      /^🔄\s*/
    ];
    const alignmentHighPatterns = [
      /^(la tua sintonia è alta|sei in armonia|le tue azioni sono allineate)/i,
      /^(your alignment is high|you're in harmony|your actions are aligned)/i,
      /^(tu sintonía es alta|estás en armonía)/i,
      /^(deine ausrichtung ist hoch|du bist im einklang)/i,
      /^(ton alignement est élevé|tu es en harmonie)/i,
      /^✅\s*/
    ];
    const alignmentLowPatterns = [
      /^(la tua sintonia è bassa|c'è un divario tra|le tue azioni non sono allineate)/i,
      /^(your alignment is low|there's a gap between|your actions aren't aligned)/i,
      /^(tu sintonía es baja|hay una brecha entre)/i,
      /^(deine ausrichtung ist niedrig|es gibt eine lücke)/i,
      /^(ton alignement est faible|il y a un écart)/i,
      /^⚠️\s*/
    ];
    const alignmentPatterns = [
      /^(la tua sintonia|l'allineamento|tra valori e azioni|i tuoi valori)[:\s]*/i,
      /^(your alignment|the alignment|between values and actions|your values)[:\s]*/i,
      /^(tu sintonía|la alineación|entre valores y acciones|tus valores)[:\s]*/i,
      /^(deine ausrichtung|zwischen werten und handlungen|deine werte)[:\s]*/i,
      /^(ton alignement|entre valeurs et actions|tes valeurs)[:\s]*/i,
      /^⚖️\s*/
    ];
    const actionPatterns = [
      /^(prova a|prova questo|potresti|un'idea:|una cosa che puoi fare)[:\s]*/i,
      /^(try this|try to|you could|one thing you can do|an idea:)[:\s]*/i,
      /^(prueba esto|prueba a|podrías|una idea:)[:\s]*/i,
      /^(probiere das|probiere mal|du könntest|eine idee:)[:\s]*/i,
      /^(essaie ça|essaie de|tu pourrais|une idée:)[:\s]*/i,
      /^✨\s*/
    ];
    
    const stripAndDetect = (line: string): { type: SectionType | null; strippedContent: string } => {
      // Check alignment levels first (more specific)
      for (const p of alignmentHighPatterns) {
        if (p.test(line)) return { type: 'insight-alignment-high', strippedContent: line.replace(p, '').trim() };
      }
      for (const p of alignmentLowPatterns) {
        if (p.test(line)) return { type: 'insight-alignment-low', strippedContent: line.replace(p, '').trim() };
      }
      for (const p of observationPatterns) {
        if (p.test(line)) return { type: 'insight-observation', strippedContent: line.replace(p, '').trim() };
      }
      for (const p of patternPatterns) {
        if (p.test(line)) return { type: 'insight-pattern', strippedContent: line.replace(p, '').trim() };
      }
      for (const p of alignmentPatterns) {
        if (p.test(line)) return { type: 'insight-alignment', strippedContent: line.replace(p, '').trim() };
      }
      for (const p of actionPatterns) {
        if (p.test(line)) return { type: 'insight-action', strippedContent: line.replace(p, '').trim() };
      }
      return { type: null, strippedContent: line };
    };

    // Process each line, but also detect insights within sentences
    const processSentences = (text: string): Array<{ type: SectionType; content: string }> => {
      const results: Array<{ type: SectionType; content: string }> = [];
      
      // Use split instead of exec to avoid potential regex issues
      const sentences = text.split(/(?<=[.!?])\s+/).filter(s => s.trim());
      
      for (const sentence of sentences) {
        const trimmedSentence = sentence.trim();
        if (!trimmedSentence) continue;
        
        const { type, strippedContent } = stripAndDetect(trimmedSentence);
        if (type) {
          results.push({ type, content: strippedContent });
        } else {
          // Combine consecutive text sections
          if (results.length > 0 && results[results.length - 1].type === 'text') {
            results[results.length - 1].content += ' ' + trimmedSentence;
          } else {
            results.push({ type: 'text', content: trimmedSentence });
          }
        }
      }
      
      // If no sentences were found, return the whole text
      if (results.length === 0 && text.trim()) {
        results.push({ type: 'text', content: text.trim() });
      }
      
      return results;
    };

    lines.forEach((line, lineIdx) => {
      const trimmed = line.trim();
      
      // Check for insight blocks first - try full line detection
      const { type: insightType, strippedContent } = stripAndDetect(trimmed);
      if (insightType) {
        if (currentText) {
          sections.push({ type: 'text', content: currentText.trim() });
          currentText = '';
        }
        sections.push({ type: insightType, content: strippedContent || trimmed });
      } else if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
        if (currentText) {
          sections.push({ type: 'text', content: currentText.trim() });
          currentText = '';
        }
        sections.push({ type: 'list', content: trimmed.replace(/^[•\-*]\s*/, '') });
      } else if (trimmed.startsWith('💡')) {
        if (currentText) {
          sections.push({ type: 'text', content: currentText.trim() });
          currentText = '';
        }
        sections.push({ type: 'action-short', content: trimmed.replace(/^💡\s*\*?\*?[^:*]+\*?\*?:?\s*/i, '') });
      } else if (trimmed.startsWith('🎯')) {
        if (currentText) {
          sections.push({ type: 'text', content: currentText.trim() });
          currentText = '';
        }
        sections.push({ type: 'action-medium', content: trimmed.replace(/^🎯\s*\*?\*?[^:*]+\*?\*?:?\s*/i, '') });
      } else if (trimmed.startsWith('🚀')) {
        if (currentText) {
          sections.push({ type: 'text', content: currentText.trim() });
          currentText = '';
        }
        sections.push({ type: 'action-long', content: trimmed.replace(/^🚀\s*\*?\*?[^:*]+\*?\*?:?\s*/i, '') });
      } else if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 10) {
        if (currentText) {
          sections.push({ type: 'text', content: currentText.trim() });
          currentText = '';
        }
        sections.push({ type: 'quote', content: trimmed.slice(1, -1) });
      } else if (trimmed.startsWith('«') && trimmed.endsWith('»')) {
        if (currentText) {
          sections.push({ type: 'text', content: currentText.trim() });
          currentText = '';
        }
        sections.push({ type: 'quote', content: trimmed.slice(1, -1) });
      } else {
        currentText += line + '\n';
      }
    });

    if (currentText.trim()) {
      const finalText = currentText.trim();
      // Process paragraphs with sentence-level insight detection
      const sentenceResults = processSentences(finalText);
      
      sentenceResults.forEach((result, idx) => {
        if (result.type !== 'text') {
          // This is an insight detected mid-paragraph
          sections.push(result);
        } else {
          // Check if the last sentence is a question
          const sentences = result.content.split(/(?<=[.!?])\s+/);
          const lastSentence = sentences[sentences.length - 1]?.trim() || '';
          
          if (idx === sentenceResults.length - 1 && lastSentence.endsWith('?') && lastSentence.length > 5) {
            const beforeQuestion = sentences.slice(0, -1).join(' ').trim();
            if (beforeQuestion) {
              sections.push({ type: 'text', content: beforeQuestion });
            }
            sections.push({ type: 'question', content: lastSentence });
          } else {
            sections.push({ type: 'text', content: result.content });
          }
        }
      });
    }

    return sections.length > 0 ? sections : [{ type: 'text' as const, content: text }];
  };

  // Parse displayed text (which may be typing in progress)
  const sections = parseContentSections(displayedText);

  return (
    <div className="flex flex-col items-start animate-in slide-in-from-left-4 fade-in duration-500">
      <div className={`max-w-[90%] lg:max-w-[75%] 2xl:max-w-[65%] rounded-[28px] lg:rounded-[36px] bg-white text-black rounded-bl-none border ${typeStyle.border} shadow-sm lg:shadow-md overflow-hidden transition-all hover:shadow-lg ${isGreeting ? 'ring-2 ring-indigo-100' : ''}`}>
        {isGreeting ? (
          <div className="px-5 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-black/[0.03] flex items-center gap-2">
            <Sparkles size={14} className="text-indigo-500" />
            <span className="text-[11px] font-bold text-indigo-600 uppercase tracking-wider">{t.lifeIntelligence}</span>
          </div>
        ) : typeStyle.showHeader && (
          <div className={`px-5 py-3 ${typeStyle.headerBg} border-b border-black/[0.03] flex items-center gap-2`}>
            <TypeIcon size={14} className={typeStyle.iconColor} />
            <span className={`text-[11px] font-bold ${typeStyle.iconColor} uppercase tracking-wider`}>{typeStyle.label}</span>
          </div>
        )}
        
        {/* Mood Indicator - Subtle colored dot showing detected emotional mode */}
        {emotionalMode !== 'neutral' && isComplete && (
          <div className="flex items-center gap-2 px-5 pb-1">
            <MoodIndicator emotionalMode={emotionalMode} />
            <span className="text-[9px] text-black/25 uppercase tracking-wide font-medium">
              {emotionalMode === 'celebration' ? (t.modeCelebration || 'celebration') : 
               emotionalMode === 'curiosity' ? (t.modeExploration || 'exploration') :
               emotionalMode === 'struggle' ? (t.modeSupport || 'support') : ''}
            </span>
          </div>
        )}

        <div className="px-5 lg:px-7 py-4 lg:py-6 space-y-3 lg:space-y-4">
          {sections.map((section, idx) => {
            if (section.type === 'highlight') {
              return (
                <div key={idx} className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100/50">
                  <p className="text-[14px] leading-relaxed font-medium text-amber-900">{section.content}</p>
                </div>
              );
            }
            if (section.type === 'list') {
              return (
                <div key={idx} className="flex items-start gap-2.5 pl-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 shrink-0" />
                  <p className="text-[14px] leading-relaxed text-black/80">{section.content}</p>
                </div>
              );
            }
            if (section.type === 'quote') {
              return (
                <div key={idx} className="relative pl-4 py-2 border-l-4 border-indigo-300 bg-indigo-50/50 rounded-r-xl">
                  <Quote size={14} className="absolute -left-0.5 -top-1 text-indigo-400 bg-white rounded-full p-0.5" />
                  <p className="text-[14px] leading-relaxed text-indigo-800 italic">"{section.content}"</p>
                </div>
              );
            }
            if (section.type === 'question') {
              return (
                <div key={idx} className={`mt-4 p-4 rounded-2xl border-2 border-dashed ${
                  responseType === 'celebration' ? 'border-emerald-200 bg-emerald-50/50' :
                  responseType === 'coaching' ? 'border-orange-200 bg-orange-50/50' :
                  responseType === 'exploration' ? 'border-amber-200 bg-amber-50/50' :
                  responseType === 'reflection' ? 'border-slate-200 bg-slate-50/50' :
                  responseType === 'insight' ? 'border-violet-200 bg-violet-50/50' :
                  'border-indigo-200 bg-indigo-50/50'
                }`}>
                  <p className={`text-[15px] leading-relaxed font-medium ${
                    responseType === 'celebration' ? 'text-emerald-800' :
                    responseType === 'coaching' ? 'text-orange-800' :
                    responseType === 'exploration' ? 'text-amber-800' :
                    responseType === 'reflection' ? 'text-slate-800' :
                    responseType === 'insight' ? 'text-violet-800' :
                    'text-indigo-800'
                  }`}>{section.content}</p>
                </div>
              );
            }
            // 3-Horizon Action Plan blocks
            if (section.type === 'action-short') {
              return (
                <div key={idx} className="flex items-start gap-3 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-sm shrink-0">
                    <span className="text-white text-sm">💡</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide mb-0.5">
                      {t.microAction || 'Today'}
                    </p>
                    <p className="text-[14px] text-emerald-800 leading-snug">{section.content}</p>
                  </div>
                </div>
              );
            }
            if (section.type === 'action-medium') {
              return (
                <div key={idx} className="flex items-start gap-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm shrink-0">
                    <span className="text-white text-sm">🎯</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wide mb-0.5">
                      This Week
                    </p>
                    <p className="text-[14px] text-amber-800 leading-snug">{section.content}</p>
                  </div>
                </div>
              );
            }
            if (section.type === 'action-long') {
              return (
                <div key={idx} className="flex items-start gap-3 p-3 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-100">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-sm shrink-0">
                    <span className="text-white text-sm">🚀</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wide mb-0.5">
                      Vision
                    </p>
                    <p className="text-[14px] text-violet-800 leading-snug">{section.content}</p>
                  </div>
                </div>
              );
            }
            // Insight blocks - visual highlights for AI observations
            if (section.type.startsWith('insight-')) {
              const insightTypeMap: Record<string, 'observation' | 'pattern' | 'alignment' | 'action'> = {
                'insight-observation': 'observation',
                'insight-pattern': 'pattern',
                'insight-alignment': 'alignment',
                'insight-alignment-high': 'alignment',
                'insight-alignment-low': 'alignment',
                'insight-action': 'action'
              };
              const alignmentLevelMap: Record<string, 'high' | 'medium' | 'low' | undefined> = {
                'insight-alignment-high': 'high',
                'insight-alignment-low': 'low',
                'insight-alignment': 'medium'
              };
              return (
                <InsightBlock 
                  key={idx}
                  content={section.content}
                  type={insightTypeMap[section.type] || 'observation'}
                  translations={t}
                  alignmentLevel={alignmentLevelMap[section.type]}
                />
              );
            }
            return (
              <p key={idx} className="text-[15px] lg:text-[16px] leading-[1.7] lg:leading-[1.85] text-black/85 whitespace-pre-wrap">
                {section.content}
                {enableTyping && !isComplete && idx === sections.length - 1 && (
                  <span className="inline-block w-0.5 h-4 lg:h-5 bg-indigo-500 ml-0.5 animate-pulse" />
                )}
              </p>
            );
          })}
        </div>

        {/* Context Sources - Discrete Apple-style "Based on..." section */}
        {hasContextUsed && isComplete && (() => {
          // Priority: goals > values > people > patterns > places > decisions > moments
          const allChips: Array<{id: string; type: 'value' | 'goal' | 'pattern' | 'moment' | 'person' | 'place' | 'decision'; label: string; typeLabel: string; citationId?: string; priority: number}> = [
            ...goalsUsed.map((g, i) => ({ id: `g-${i}`, type: 'goal' as const, label: g, typeLabel: t.goals || 'goals', priority: 6 })),
            ...valuesUsed.map((v, i) => ({ id: `v-${i}`, type: 'value' as const, label: v, typeLabel: t.values || 'values', priority: 5 })),
            ...peopleUsed.map((p, i) => ({ id: `pe-${i}`, type: 'person' as const, label: p, typeLabel: t.people || 'people', priority: 4 })),
            ...patternsUsed.map((p, i) => ({ id: `p-${i}`, type: 'pattern' as const, label: p, typeLabel: t.patterns || 'patterns', priority: 3 })),
            ...placesUsed.map((p, i) => ({ id: `pl-${i}`, type: 'place' as const, label: p, typeLabel: t.places || 'places', priority: 2 })),
            ...decisionsUsed.map((d, i) => ({ id: `d-${i}`, type: 'decision' as const, label: d, typeLabel: t.decisions || 'decisions', priority: 1 })),
            ...citedEntries.filter(c => c?.snippet).map((c, i) => ({ id: `c-${i}`, type: 'moment' as const, label: (c.snippet || '').substring(0, 30) + '...', typeLabel: t.moments || 'moments', citationId: c.id, priority: 0 }))
          ].sort((a, b) => b.priority - a.priority);
          
          // Show only 2 items by default for discrete appearance
          const MAX_VISIBLE = 2;
          const isExpanded = expandedChip === 'show-all';
          const visibleChips = isExpanded ? allChips : allChips.slice(0, MAX_VISIBLE);
          const hiddenCount = allChips.length - MAX_VISIBLE;
          const totalSources = allChips.length;
          
          return (
            <div className="px-5 lg:px-7 py-2.5 border-t border-black/[0.02] bg-gradient-to-b from-transparent to-black/[0.01]">
              {/* Discrete header with source count */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-black/20" />
                  <span className="text-[9px] font-medium text-black/35 uppercase tracking-wider">
                    {t.basedOn || 'Based on'}
                  </span>
                </div>
                {!isExpanded && totalSources > MAX_VISIBLE && (
                  <span className="text-[9px] text-black/25">
                    {totalSources} {t.sources || 'sources'}
                  </span>
                )}
              </div>
              
              {/* Context chips */}
              <div className="flex flex-wrap items-center gap-1.5">
                {visibleChips.map((chip) => (
                  <ContextChip
                    key={chip.id}
                    type={chip.type}
                    label={chip.label}
                    typeLabel={chip.typeLabel}
                    isExpanded={expandedChip === chip.id}
                    onTap={() => {
                      setExpandedChip(expandedChip === chip.id ? null : chip.id);
                      if (chip.citationId) {
                        setExpandedCitation(expandedCitation === chip.citationId ? null : chip.citationId);
                      }
                    }}
                  />
                ))}
                {hiddenCount > 0 && !isExpanded && (
                  <button
                    onClick={() => setExpandedChip('show-all')}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium text-black/40 hover:text-black/60 hover:bg-black/[0.03] transition-all"
                  >
                    +{hiddenCount}
                  </button>
                )}
                {isExpanded && hiddenCount > 0 && (
                  <button
                    onClick={() => setExpandedChip(null)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium text-black/40 hover:text-black/60 hover:bg-black/[0.03] transition-all"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          );
        })()}
        
        {/* Expanded Citation Preview - Inline, not modal */}
        {expandedCitation && citedEntries.find(c => c.id === expandedCitation) && (
          <div className="mx-5 lg:mx-7 mb-3 p-3 bg-indigo-50/50 rounded-xl border-l-2 border-indigo-300 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-[10px]">📖</span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-medium text-indigo-600">
                  {citedEntries.find(c => c.id === expandedCitation)?.date}
                </span>
                <p className="text-[12px] text-indigo-800 mt-0.5 leading-relaxed">
                  "{citedEntries.find(c => c.id === expandedCitation)?.snippet}"
                </p>
              </div>
              <button 
                onClick={() => setExpandedCitation(null)}
                className="text-indigo-400 hover:text-indigo-600 p-1"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {actions.length > 0 && isComplete && (
          <div className="px-5 pb-4 pt-2 space-y-2.5 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
            {actions.map((action, idx) => {
              const executed = isExecuted?.(action.title) ?? false;
              const actionGradient = executed 
                ? 'bg-emerald-50 border border-emerald-100' 
                : action.type === 'VALUE'
                  ? 'bg-gradient-to-r from-rose-500 to-pink-500 shadow-lg shadow-rose-200/50'
                  : action.type === 'GOAL'
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-500 shadow-lg shadow-teal-200/50'
                    : responseType === 'celebration' 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 shadow-lg shadow-emerald-200/50'
                      : responseType === 'coaching'
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 shadow-lg shadow-orange-200/50'
                        : responseType === 'exploration'
                          ? 'bg-gradient-to-r from-amber-500 to-yellow-500 shadow-lg shadow-amber-200/50'
                          : responseType === 'reflection'
                            ? 'bg-gradient-to-r from-slate-500 to-gray-500 shadow-lg shadow-slate-200/50'
                            : responseType === 'insight'
                              ? 'bg-gradient-to-r from-violet-500 to-purple-500 shadow-lg shadow-violet-200/50'
                              : 'bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-indigo-200/50';
              return (
                <button
                  key={idx}
                  onClick={() => onAction?.(action.type, action.title)}
                  disabled={executed}
                  className={`w-full p-3.5 rounded-2xl flex items-center gap-3 transition-all active:scale-[0.98] ${actionGradient}`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                    executed ? 'bg-emerald-100' : 'bg-white/20'
                  }`}>
                    {executed ? (
                      <CheckCircle size={18} className="text-emerald-600" />
                    ) : action.type === 'TRACK' ? (
                      <PlusCircle size={18} className="text-white" />
                    ) : action.type === 'VALUE' ? (
                      <Heart size={18} className="text-white" />
                    ) : action.type === 'GOAL' ? (
                      <Target size={18} className="text-white" />
                    ) : (
                      <Compass size={18} className="text-white" />
                    )}
                  </div>
                  <div className="text-left flex-1">
                    <span className={`text-[9px] font-bold uppercase tracking-widest ${
                      executed ? 'text-emerald-500' : 'text-white/70'
                    }`}>
                      {executed ? t.saved : 
                       action.type === 'TRACK' ? t.rememberThis : 
                       action.type === 'VALUE' ? (t.saveValue || 'Save Value') :
                       action.type === 'GOAL' ? (t.saveGoal || 'Save Goal') :
                       t.goDeeper}
                    </span>
                    <p className={`text-[13px] font-bold leading-snug ${
                      executed ? 'text-emerald-700' : 'text-white'
                    }`}>{action.title}</p>
                    {action.reason && !executed && (
                      <p className="text-[10px] text-white/60 mt-0.5 leading-snug">{action.reason}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <span className="text-[10px] font-bold text-black/15 uppercase mt-2.5 tracking-widest px-3">{timestamp}</span>
    </div>
  );
};

export default ChatCard;